import { HTMLParser } from 'Utils/Parser';
import { enableYearlyDiscountExperiment, replaceSKUComboExperiment } from 'Utils/Analytics/Amplitude';
import {
  getFreeSheerIdPlanFromType,
  isMilitaryUpgradePlan,
  isSheerIdFreePlan,
  isFirstResponderChurned,
  isFirstResponderUpgradePlan,
  isMilitaryChurned,
  displaySheerIDUpgradePlans,
  isSheerIdPlan,
} from 'Utils/SheerIdHelpers';
import {
  DISABLE_SHIPPING_ADDRESS_PROMOS,
  FOUNDERS_CUTOFF_DATE,
  FOUNDERS_SERVICE_ID,
  FIRST_MONTH_FREE_PROMO,
  FIRST_MONTH_99C_PROMO,
  MONTHLY_ROKT_SERVICE_ID,
  EASTER_2_YEAR_ID,
  EASTER_SEGMENT_TAG,
  SHEER_MILITARY_PACKAGE_ID,
  SHEER_FIRST_RESPONDER_PACKAGE_ID,
} from './Constants';

export const isFounderServiceId = (serviceId) => serviceId === FOUNDERS_SERVICE_ID;
export const isFoundingMember = (serviceId, startDate) =>
  isFounderServiceId(serviceId) && startDate < FOUNDERS_CUTOFF_DATE;

// utils
export function getFrequencyLabel({ duration, period }) {
  if (!duration || !period) return '';

  if (period === 'NoOfYears') {
    return duration > 1 ? `${duration} yrs` : `yr`;
  }
  if (period === 'NoOfMonths') {
    return duration > 1 ? `${duration} mos` : `mo`;
  }
  return '';
}

export const getPlanSelectFrequencyLabel = (plan) => {
  let per = '/';
  let time = plan.period === 'NoOfYears' ? 'yr' : 'mo';
  let plural = plan.duration > 1 ? 's' : '';

  let result = `/${plan.duration > 1 ? plan.duration : ''}${time}${plural}`;

  if (plan.hasPrePayPromotion) {
    per = ' for ';
    result = `${per}${plan.duration}${time}${plural}`;
  }
  return result;
};

export const getPlanDisplayPrice = (planPrice) => {
  if (!planPrice) return '';

  const price = planPrice.toFixed(2);
  const [dollars, cents] = price.split('.');

  return `$${dollars}${Number(cents) > 0 ? `.${cents}` : ''}`;
};

//Not to be used in Billing History or Invoice screens
export const getSimplifiedPlanDisplayName = (plan) => {
  let planName = 'Monthly';
  if (plan.period === 'NoOfYears') {
    if (plan.duration > 1) {
      planName = '2 Year';
    } else {
      planName = 'Yearly';
    }
  }
  return planName;
};

export const getActivePlanSimplifiedName = (plans = [], activePlanId = '') => {
  const activePlan = plans.filter((plan) => plan.planId === activePlanId)[0];
  if (activePlan) {
    return getSimplifiedPlanDisplayName(activePlan);
  }
  return null;
};

export const getPlanButtonText = (hasSubscription, plan, ctaText) => {
  let linkText = 'Select Plan';
  if (ctaText) {
    linkText = ctaText;
  } else if (hasSubscription) {
    linkText = 'Switch to This Plan';
  } else if (is7DayFreeTrial(plan)) {
    linkText = 'Start Your 7-Day Free Trial';
  } else if (isFreeTrial(plan)) {
    linkText = 'Start Your Free Trial';
  }

  return linkText;
};

const prePayPromotion = ({ promotionCategory = '' }) => promotionCategory === 'PrePay';

const freePromotions = ({ isFreeTrail }) => isFreeTrail;

/* The isFreeTrail field from Evergent is unreliable - e.g. the 1STMONTH99C (1st month for 99cents)
   has isFreeTrail = true. This function helps determine if it is actually a free trial
   for FE display purposes  */
export const isFreeTrial = (plan) => {
  return plan?.hasFreePromo && plan?.freePromo && !(plan?.freePromo.amount > 0);
};

export const is7DayFreeTrial = (plan) => {
  return plan?.hasFreePromo && plan?.freePromo?.period === 'NoOfDays' && plan?.freePromo?.duration === 8;
};

const promotionalItem = ({ PromotionalItem = [] }) => {
  const hasPromotionalItems = Array.isArray(PromotionalItem) && PromotionalItem.length;
  if (hasPromotionalItems) {
    // temporary disabling shipping form. (roku promo have digital merch, so it does not require a shipping address)
    // Evergent is going to provide a flag at promo level to reflect non-digital merchandise
    const validPromotionalItems = PromotionalItem.some(
      (promo) => !DISABLE_SHIPPING_ADDRESS_PROMOS.includes(promo.serviceId)
    );
    return validPromotionalItems;
  }
  return false;
};

export const mapPlan = ({ promoMsg, pkgMsg = {} }) => {
  const promos = promoMsg || [];
  const hasPromotionalItems = promos.some(promotionalItem);
  const hasPrePayPromotion = promos.some(prePayPromotion);
  const hasFreePromo = promos.some(freePromotions);
  const prePayPromo = promos.find(prePayPromotion); // amount,duration,period,promotionID
  const freePromo = promos.find(freePromotions); // displayName,nextBillingDateTime,promotionID
  const hasMerchandise = hasPromotionalItems && hasPrePayPromotion;

  const { appId, packageID, tier, displayName, packageName, bbDescription, retailPrice, duration, period } = pkgMsg;

  const planName = displayName;
  const planPrice = hasPrePayPromotion ? prePayPromo.amount : retailPrice;
  const planFrequencyText = hasPrePayPromotion
    ? getFrequencyLabel(prePayPromo)
    : getFrequencyLabel({ duration, period });
  let promotionID = hasPrePayPromotion ? prePayPromo.promotionID : '';
  let frequency = hasPrePayPromotion ? prePayPromo : { duration, period };
  if (isSheerIdFreePlan(packageID) && freePromo) {
    promotionID = freePromo.promotionID || '';
    frequency = freePromo;
  }

  return {
    appId,
    promotionID,
    packageID,
    planId: packageID,
    tier,
    planName,
    packageName,
    duration: frequency.duration,
    period: frequency.period,
    planDescription: bbDescription,
    planPrice,
    hasMerchandise,
    planFrequencyText,
    freePromo,
    hasFreePromo,
    hasPrePayPromotion,
  };
};

export const mapPackages = (packages = []) => packages.map(mapPlan);

// expland on this function if more amplitude experiments get added to signupplans;
export const addAmplitudeExperiments = (validPlans, allPlans) => {
  let experimentPlans = [...validPlans];
  experimentPlans = enableYearlyDiscountExperiment(experimentPlans);
  experimentPlans = replaceSKUComboExperiment(experimentPlans, allPlans);
  return experimentPlans;
};

// returns Plans[]|Array(Plan)
export const getAvailableSignUpPlans = (plans = [], defaultPlans = [], subscription = {}, query = '') => {
  let plansToDisplay = defaultPlans;
  const { accountPromotionSegment } = subscription;

  //if had a first responder plan previously, only show eligible first responder upgrade plans
  if (isFirstResponderChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
    plansToDisplay = plansToDisplay.filter((planId) => isFirstResponderUpgradePlan(planId));
    //if had a military plan previously, only show eligible military upgrade plans
  } else if (isMilitaryChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
    plansToDisplay = plansToDisplay.filter((planId) => isMilitaryUpgradePlan(planId));
    //if newly verified in sheerid and hasn't got a free sheerid plan yet, show the free sheerid plan:
  } else if (getFreeSheerIdPlanFromType()) {
    plansToDisplay = [getFreeSheerIdPlanFromType()];
    //if rokt sku is selected, that is the only option to show
  } else if (query.toUpperCase() === MONTHLY_ROKT_SERVICE_ID) {
    plansToDisplay = [MONTHLY_ROKT_SERVICE_ID];
  }

  const validPlans = plans.filter((p) => plansToDisplay.includes(p.planId));

  return addAmplitudeExperiments(validPlans, plans);
};

// returns Plans[]|Array(Plan)
export const getAvailableChangePlans = (plans = [], defaultPlans = [], subscription = {}, query = '') => {
  const { activeSubscriptionId = '', accountPromotionSegment } = subscription;
  let plansToDisplay = defaultPlans;

  //if has an active first responder or military plan, only show eligible upgrade plans
  if (isSheerIdPlan(activeSubscriptionId)) {
    plansToDisplay = displaySheerIDUpgradePlans(subscription, plans, activeSubscriptionId);
    //if newly verified in sheerid, show the free sheerid plan:
  } else if (getFreeSheerIdPlanFromType()) {
    plansToDisplay = [getFreeSheerIdPlanFromType()];
    // if user has easter segment tag and the sku is selected, only show and allow checkout with this plan
  } else if (accountPromotionSegment.segmentType === EASTER_SEGMENT_TAG && query.toUpperCase() === EASTER_2_YEAR_ID) {
    plansToDisplay = [...plansToDisplay, EASTER_2_YEAR_ID];
  }

  plansToDisplay = [activeSubscriptionId, ...plansToDisplay]; // always include active plan on change plans.

  const availablePlans = plans.filter((p) => plansToDisplay.includes(p.planId));

  //final logic to determine if should return empty array indicating change is unavailable
  const hasOnlyPlanAvailable = availablePlans.every((plan) => plan.planId === subscription.activeSubscriptionId);
  const changeUnavailable = hasOnlyPlanAvailable;
  return changeUnavailable ? [] : availablePlans;
};

export const getPlanFromList = (query = '', plans = [], activeSubscriptionId = '') => {
  const matchedPlan = plans.find((plan) => {
    const { planName = '', packageID = '' } = plan;
    //Don't match if no query of if existing user already has the plan
    if (!query || activeSubscriptionId === packageID) return false;
    const preSelectedPlan = query.toLowerCase();
    return planName.toLowerCase().startsWith(preSelectedPlan) || preSelectedPlan === packageID.toLowerCase();
  });
  return matchedPlan || {};
};

export const isFreeMonthPromoPlanActive = (selectedPlan) => {
  const { freePromo } = selectedPlan || {};
  if (freePromo) {
    return freePromo.promotionID === FIRST_MONTH_FREE_PROMO;
  }
  return false;
};

/*
  Helper to reduce plans (from /packages) and PlanStrings.json
  Signup plans uses `planBenefitsV2` or  `plansCopyV3` config object.
*/
export const getPlansWithConfigStrings = (plans, configs, isPlansCopyV3 = false) => {
  const planObjType = isPlansCopyV3 ? 'plansCopyV3' : 'planBenefitsV2';
  return plans.reduce((acc, plan) => {
    const config = (configs[plan.planId] && configs[plan.planId][planObjType]) || {};
    const configOverride = plan.configOverride || {};
    return [
      ...acc,
      {
        ...plan,
        config: { ...config, ...configOverride },
      },
    ];
  }, []);
};

export const isMonthlyPromoPlanActive = (selectedPlan) => {
  const { freePromo } = selectedPlan || {};
  if (freePromo) {
    return freePromo.promotionID === FIRST_MONTH_99C_PROMO;
  }
  return false;
};

/* TODO remove if no longer used */
/* for now used to insert line break for first responder packages with long names
  used in signup-plans desktop view and change-plans */
export const planNameFormatter = (planName) => {
  let name = planName;
  if (name.includes('First Responder ')) {
    name = name.split('First Responder ').join('First Responder<br>');
  } else if (name.includes('First Responders ')) {
    name = name.split('First Responders ').join('First Responders<br>');
  }
  return HTMLParser(name);
};
